import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { LOGO_URL } from '../../data/mock';

const navLinks = [
  { label: 'Membership', href: '/#membership' },
  { label: 'The Table', href: '/#the-table' },
  { label: 'Is This For Me', href: '/#who' },
];

const orgSubLinks = [
  { label: 'Venture Capital', href: '/for-organizations/venture-capital' },
  { label: 'Accelerators', href: '/for-organizations/accelerators' },
  { label: 'Community Organizations', href: '/for-organizations/communities' },
  { label: 'Fellowship & Cohort Programs', href: '/for-organizations/cohorts' },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const [mobileOrgOpen, setMobileOrgOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOrgOpen(false); };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const [path, hash] = href.split('#');
    if (location.pathname !== '/' && path === '/') {
      navigate('/', { state: { scrollTo: hash } });
    } else if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.state]);

  return (
    <>
      {/* Copper top rule */}
      <div className="sticky top-0 z-[51] h-[3px] bg-[#b5813b] w-full" />
      <header className={`sticky top-[3px] z-50 transition-all duration-500 ${scrolled ? 'bg-vine-primary/[0.98] backdrop-blur-lg border-b border-white/[0.08]' : 'bg-vine-primary/[0.97] backdrop-blur-lg'}`}>
        <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex-shrink-0 flex items-center gap-3">
            <img src={LOGO_URL} alt="Copper & Vine" className="h-11 w-auto" />
            <span className="hidden sm:block text-[10px] tracking-[0.2em] uppercase text-[#888888] font-body mt-1">Advisory Circle</span>
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} onClick={e => handleAnchorClick(e, link.href)} className="text-[13px] tracking-[0.08em] uppercase font-body text-vine-neutral/70 hover:text-copper transition-colors duration-300">{link.label}</a>
            ))}

            {/* For Organizations Dropdown */}
            <div ref={dropdownRef} className="relative" onMouseEnter={() => setOrgOpen(true)} onMouseLeave={() => setOrgOpen(false)}>
              <button onClick={() => setOrgOpen(!orgOpen)} className={`text-[13px] tracking-[0.08em] uppercase font-body transition-colors duration-300 flex items-center gap-1 py-6 ${location.pathname.startsWith('/for-organizations') ? 'text-copper' : 'text-vine-neutral/70 hover:text-copper'}`}>
                For Organizations <ChevronDown size={12} className={`transition-transform duration-200 ${orgOpen ? 'rotate-180' : ''}`} />
              </button>
              {orgOpen && (
                <div className="absolute top-full left-0 pt-0 w-64">
                  <div className="bg-vine-primary/[0.98] backdrop-blur-lg border border-white/[0.08] py-2 animate-fade-in">
                    {orgSubLinks.map(link => (
                      <Link key={link.href} to={link.href} onClick={() => setOrgOpen(false)} className="block px-5 py-2.5 text-[13px] text-vine-neutral/65 hover:text-copper hover:bg-white/[0.03] transition-colors">{link.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-7">
            <Link to="/apply" className="px-7 py-2.5 bg-[#b5813b] text-white text-[14px] tracking-[0.08em] uppercase hover:bg-copper-dark transition-colors duration-300">Apply</Link>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-vine-neutral/80 hover:text-white transition-colors">{mobileOpen ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>

        {mobileOpen && (
          <div className="lg:hidden bg-vine-primary border-t border-white/[0.06] animate-fade-in">
            <div className="px-6 py-8 flex flex-col gap-1">
              {navLinks.map(link => (
                <a key={link.href} href={link.href} onClick={e => handleAnchorClick(e, link.href)} className="py-3 text-[14px] tracking-[0.05em] text-vine-neutral/70 hover:text-copper transition-colors">{link.label}</a>
              ))}
              {/* Mobile org accordion */}
              <button onClick={() => setMobileOrgOpen(!mobileOrgOpen)} className="py-3 text-[14px] tracking-[0.05em] text-vine-neutral/70 hover:text-copper transition-colors flex items-center justify-between">
                For Organizations <ChevronDown size={14} className={`transition-transform ${mobileOrgOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileOrgOpen && (
                <div className="pl-4 space-y-1 pb-2">
                  {orgSubLinks.map(link => (
                    <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)} className="block py-2 text-[13px] text-vine-neutral/55 hover:text-copper transition-colors">{link.label}</Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-vine-primary/[0.98] backdrop-blur-lg border-t border-white/[0.08] p-3 px-5">
        <Link to="/apply" className="block w-full py-3 bg-copper text-white text-center text-[13px] tracking-[0.1em] uppercase btn-copper">Apply for Membership</Link>
      </div>
    </>
  );
};
